import { acceptedRedirectHosts } from '@gonfalon/dogfood-flags/dogfood-flags';
import { predicateFor } from '@gonfalon/legacy-form-validation/validationUtils';
import { startsWith } from '@gonfalon/strings/startsWith';
import qs from 'qs';

export const DEFAULT_REDIRECT_PATH = '/';

export function isAbsolute(url: string) {
  // cannot use the parseURL above since the browser automatically prepends the current
  // page's protocol by default
  return /^(?:\w+:)?\/\//.test(url.toString());
}

export function isSubpathOrSelf(currentUrl: string, url: string) {
  return currentUrl === url || startsWith(currentUrl, url);
}

export function addQueryParams(url: string, additionalParams: object) {
  const [path, queryString] = url.split('?');
  const params = qs.parse(queryString);
  const withRelayState = qs.stringify({
    ...params,
    ...additionalParams,
  });
  return `${path}?${withRelayState}`;
}

export function removeQueryParam(queryString: string, clearedParam: string) {
  const parsedQueryString = qs.parse(queryString, { ignoreQueryPrefix: true });
  delete parsedQueryString[clearedParam];
  return qs.stringify(parsedQueryString, { indices: false });
}

export function isValidPort(url: string) {
  try {
    const port = new URL(url).port;
    return port === '' || port === '80' || port === '443';
  } catch {
    return false;
  }
}

export const urlWithRelayState = (url: string, relayUrl: string) => addQueryParams(url, { RelayState: relayUrl });

export const isAbsoluteURL = predicateFor(
  (v: string) => !isAbsolute(v.toLowerCase()),
  () => 'This is not an absolute URL',
);

export const isValidURLPort = predicateFor(
  (v: string) => !isValidPort(v.toLowerCase()),
  () => 'This url contains a port that is not allowed. Use either port 80 or 443',
);

export const getIsValidRedirectPath = (redirectPath: string) => {
  if (!redirectPath || redirectPath.trim() === '') {
    return false;
  }

  let url;
  let redirectPathValid = false;
  try {
    url = new URL(redirectPath);
  } catch (_) {
    try {
      url = new URL(redirectPath, window.location.origin);
    } catch (__) {
      return false;
    }
  }
  if (url) {
    const redirectHosts = acceptedRedirectHosts() || [];
    const accepted = [window.location.host, ...redirectHosts];
    const allowedProtocols = ['https:', 'http:'];
    redirectPathValid = accepted.includes(url.host) && allowedProtocols.includes(url.protocol);
  }
  return redirectPathValid;
};

/**
 * Merges additional query parameters into a redirect path, handling both relative and absolute URLs.
 * Returns the original path unchanged if:
 * - The path is empty/undefined
 * - There are no params to merge
 * - The path cannot be parsed as a valid URL
 *
 * @param redirectPath - The base path (relative like "/dashboard?foo=bar" or absolute like "https://example.com")
 * @param paramsToMerge - URLSearchParams containing additional parameters to merge
 * @returns The path with merged query parameters, or the original path if merging isn't possible
 */
export function mergeQueryParamsIntoPath(redirectPath: string | undefined, paramsToMerge: URLSearchParams): string {
  if (!redirectPath) {
    return '';
  }

  // If no params to merge, return redirectPath as-is to avoid URL normalization (e.g., adding trailing slash)
  if (paramsToMerge.size === 0) {
    return redirectPath;
  }

  try {
    const url = new URL(redirectPath, window.location.origin);
    paramsToMerge.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    // For absolute URLs (external redirects), preserve the full href; for relative paths, use pathname + search + hash
    const lowerPath = redirectPath.toLowerCase();
    const isAbsoluteUrl = lowerPath.startsWith('http://') || lowerPath.startsWith('https://');
    return isAbsoluteUrl ? url.href : url.pathname + url.search + url.hash;
  } catch {
    // If URL parsing fails, return the original path unchanged
    return redirectPath;
  }
}
